import { useEffect, useState } from 'react';
import { getMedicalRecordRevisions } from '../../../../api/patients';
import { ehr } from '../../styles/ehrClasses';
import { formatDateTime } from '../../utils/ehrUtils';

function formatRole(role) {
  if (!role) return '';
  return String(role).replace(/_/g, ' ');
}

function textOrDash(value) {
  if (value == null || value === '') return '—';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function FieldDiff({ label, oldValue, newValue }) {
  const oldText = textOrDash(oldValue);
  const newText = textOrDash(newValue);
  if (oldText === newText) return null;
  return (
    <div className="mt-2 grid gap-2 sm:grid-cols-2">
      <div className="rounded-lg border border-amber-200 bg-amber-50/80 p-2.5">
        <p className="text-[0.65rem] font-bold uppercase tracking-wide text-amber-800">
          Previous {label}
        </p>
        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{oldText}</p>
      </div>
      <div className="rounded-lg border border-emerald-200 bg-emerald-50/80 p-2.5">
        <p className="text-[0.65rem] font-bold uppercase tracking-wide text-emerald-800">
          New {label}
        </p>
        <p className="mt-1 whitespace-pre-wrap text-sm text-slate-800">{newText}</p>
      </div>
    </div>
  );
}

/**
 * Shows medical-record correction history: old vs new + who edited.
 */
export default function ConsultationRevisionHistory({
  patientId,
  consultationId,
  resourceType = 'consultation',
}) {
  const [revisions, setRevisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!patientId || !consultationId) {
      setRevisions([]);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);
    setError('');

    getMedicalRecordRevisions(patientId, {
      resource_type: resourceType,
      resource_id: consultationId,
    })
      .then((rows) => {
        if (!cancelled) setRevisions(Array.isArray(rows) ? rows : []);
      })
      .catch((err) => {
        if (!cancelled) {
          setError(err.message || 'Failed to load edit history');
          setRevisions([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [patientId, consultationId, resourceType]);

  if (loading) {
    return <p className="mt-2 text-xs text-slate-500">Loading edit history…</p>;
  }

  if (error) {
    return (
      <p className="mt-2 text-xs text-amber-800" role="status">
        {error}
      </p>
    );
  }

  if (!revisions.length) return null;

  return (
    <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
      <h5 className={ehr.sectionTitle}>Record corrections</h5>
      <ul className="mt-2 space-y-3">
        {revisions.map((rev) => (
          <li key={rev.id} className="border-l-2 border-slate-300 pl-3">
            <p className="text-xs text-slate-600">
              Edited by{' '}
              <span className="font-semibold text-slate-900">
                {rev.edited_by_name || 'Unknown'}
              </span>
              {rev.edited_by_role ? ` (${formatRole(rev.edited_by_role)})` : ''}
              {rev.edited_at ? ` · ${formatDateTime(rev.edited_at)}` : ''}
            </p>
            {rev.reason ? (
              <p className="mt-1 text-xs text-slate-700">
                <span className="font-semibold">Reason: </span>
                {rev.reason}
              </p>
            ) : null}
            {resourceType === 'registration_medical_history' ? (
              <FieldDiff
                label="medical history"
                oldValue={rev.previous_value}
                newValue={rev.new_value}
              />
            ) : (
              <>
                <FieldDiff
                  label="diagnosis"
                  oldValue={rev.previous_value?.diagnosis}
                  newValue={rev.new_value?.diagnosis}
                />
                <FieldDiff
                  label="notes"
                  oldValue={rev.previous_value?.notes}
                  newValue={rev.new_value?.notes}
                />
                <FieldDiff
                  label="actions"
                  oldValue={rev.previous_value?.actions_taken}
                  newValue={rev.new_value?.actions_taken}
                />
                <FieldDiff
                  label="dental exam"
                  oldValue={rev.previous_value?.dental_exam}
                  newValue={rev.new_value?.dental_exam}
                />
              </>
            )}
          </li>
        ))}
      </ul>
    </div>
  );
}
